/**
 * Dashboard grid layout with row-based drag-and-drop and per-section widths.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  useDroppable,
  CollisionDetection,
  pointerWithin,
  useDndMonitor,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { KpiSection } from '@/components/dashboard/sections/KpiSection';
import { PostItsSection } from '@/components/dashboard/sections/PostItsSection';
import { SessionsSection } from '@/components/dashboard/sections/inperson/SessionsSection';
import { EstablishmentsSection } from '@/components/dashboard/sections/EstablishmentsSection';
import { TutorsSection as TutorsInPersonSection } from '@/components/dashboard/sections/inperson/TutorsSection';
import { StudentsSection as StudentsInPersonSection } from '@/components/dashboard/sections/inperson/StudentsSection';
import { VolunteersSection } from '@/components/dashboard/sections/VolunteersSection';
import { AdminsSection } from '@/components/dashboard/sections/AdminsSection';
import { CalendarSection } from '@/components/dashboard/sections/CalendarSection';
import { SessionsChartSection } from '@/components/dashboard/sections/SessionsChartSection';
import { ResourcesChartSection } from '@/components/dashboard/sections/ResourcesChartSection';
import { ReportsSection } from '@/components/dashboard/sections/inperson/ReportsSection';
import { VscReportsSection } from '@/components/dashboard/sections/VscReportsSection';
import { AssessmentsSection } from '@/components/dashboard/sections/AssessmentsSection';
import { SubjectsSection } from '@/components/dashboard/sections/SubjectsSection';
import { LevelsSection } from '@/components/dashboard/sections/LevelsSection';
import { ProgramsSection } from '@/components/dashboard/sections/ProgramsSection';
import { SchoolYearsSection } from '@/components/dashboard/sections/SchoolYearsSection';
import { ActivityTypesSection } from '@/components/dashboard/sections/ActivityTypesSection';
import { ActivitiesSection } from '@/components/dashboard/sections/ActivitiesSection';
import { HolidaysSection } from '@/components/dashboard/sections/HolidaysSection';
import { EventsAndSummariesSection } from '@/components/dashboard/sections/EventsAndSummariesSection';
import { PairsSection } from '@/components/dashboard/sections/inperson/PairsSection';
import { PairsSection as PairsRemoteSection } from '@/components/dashboard/sections/remote/PairsSection';
import { TutorsSection as TutorsRemoteSection } from '@/components/dashboard/sections/remote/TutorsSection';
import { StudentsSection as StudentsRemoteSection } from '@/components/dashboard/sections/remote/StudentsSection';
import { ParentsSection } from '@/components/dashboard/sections/ParentsSection';
import { PartnersSection } from '@/components/dashboard/sections/PartnersSection';
import { SportClubsSection } from '@/components/dashboard/sections/SportClubsSection';
import { SessionsSection as SessionsRemoteSection } from '@/components/dashboard/sections/remote/SessionsSection';
import { ReportsSection as ReportsRemoteSection } from '@/components/dashboard/sections/remote/ReportsSection';
import {
  DropZoneBetweenRows,
  SourceRowPlaceholder,
  InlineDropPlaceholder,
} from '@/components/dashboard/DashboardDropZone';
import { Section } from '@/components/common/Section';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { cn } from '@/lib/utils';
import type { ElementWidth } from '@/types/interface-elements.types';
import { getSectionIEResource, getSectionIcon } from '@/utils/dashboard-ie-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { useMode } from '@/contexts/ModeContext';
import { useInterfaceElementsStore } from '@/stores/interfaceElementsStore';
import { getInterfaceElements } from '@/services/interface-elements.service';
import type { DashboardSection } from '@/types/dashboard.types';
import {
  DASHBOARD_DEFAULT_SOURCE_SECTION_HEIGHT,
  DASHBOARD_MAX_SECTIONS_PER_ROW,
  DASHBOARD_OVERLAY_OFFSET_X,
  DASHBOARD_OVERLAY_OFFSET_Y,
  DASHBOARD_WIDTH_CLASSES,
  SOURCE_ROW_PLACEHOLDER_ID,
} from './dashboard-configs';
import {
  applyEqualRowWidths,
  applySectionDragEnd,
  applySectionResize,
  buildRowItems,
  calculateAllRowsPreview,
  calculateRows,
  computeRowInsertIdx,
  computeRowInsertIndicator,
  getLucideIcon,
  isDropZoneAfterRowId,
  isRowDroppableId,
  parseRowDroppableIdx,
  resolveVisualItems,
  rowDroppableId,
  type VisualRow,
} from './dashboard-utils';

/** Props for {@link DashboardLayout}. */
export interface DashboardLayoutProps {
  sections: DashboardSection[];
  onSectionsChange: (sections: DashboardSection[]) => void;
  editMode: boolean;
  previewMode?: boolean;
  scale?: number;
  onUnsavedChanges?: (hasChanges: boolean) => void;
  onToggleVisibility?: (sectionId: string) => void;
}

/** Tracks a `position: fixed` drag overlay glued to the cursor. */
function useCustomDragOverlay(activeId: string | null) {
  const [position, setPosition] = React.useState<{ x: number; y: number } | null>(null);
  const rafRef = React.useRef<number | null>(null);

  useDndMonitor({
    onDragStart(event) {
      document.body.style.cursor = 'grabbing';
      if (event.activatorEvent && 'clientX' in event.activatorEvent) {
        const e = event.activatorEvent as PointerEvent;
        setPosition({
          x: e.clientX + DASHBOARD_OVERLAY_OFFSET_X,
          y: e.clientY + DASHBOARD_OVERLAY_OFFSET_Y,
        });
      }
    },
    onDragEnd() {
      document.body.style.cursor = '';
      setPosition(null);
    },
    onDragCancel() {
      document.body.style.cursor = '';
      setPosition(null);
    },
  });

  React.useEffect(() => {
    if (!activeId) setPosition(null);
  }, [activeId]);

  const updateFromPointer = React.useCallback((clientX: number, clientY: number) => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      setPosition({
        x: clientX + DASHBOARD_OVERLAY_OFFSET_X,
        y: clientY + DASHBOARD_OVERLAY_OFFSET_Y,
      });
      rafRef.current = null;
    });
  }, []);

  React.useEffect(() => {
    if (!activeId) return;

    const handlePointerMove = (e: PointerEvent) => {
      updateFromPointer(e.clientX, e.clientY);
    };

    document.addEventListener('pointermove', handlePointerMove, { capture: true });

    return () => {
      document.removeEventListener('pointermove', handlePointerMove, { capture: true });
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [activeId, updateFromPointer]);

  return position;
}

/** Custom drag overlay (rotated chip) portaled to `document.body`. */
const DashboardDragOverlayPortal: React.FC<{
  activeId: string | null;
  sections: DashboardSection[];
}> = ({ activeId, sections }) => {
  const overlayPosition = useCustomDragOverlay(activeId);

  if (!activeId || !overlayPosition) return null;

  const activeSection = sections.find((s) => s.id === activeId);
  if (!activeSection) return null;

  const SectionIcon = getLucideIcon(getSectionIcon(activeSection.type));

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: overlayPosition.x,
        top: overlayPosition.y,
        zIndex: 9999,
        opacity: 0.8,
        transform: 'rotate(-3deg)',
        pointerEvents: 'none',
      }}
      className="bg-background border-2 border-primary rounded-xl p-4 shadow-2xl"
    >
      <div className="flex items-center gap-3">
        <SectionIcon className="h-6 w-6 text-primary" />
        <span className="font-semibold">{activeSection.title}</span>
      </div>
    </div>,
    document.body,
  );
};

/** Defer section mount + IE fetch until the placeholder is near the viewport. */
const LazyRender: React.FC<{ children: React.ReactNode; interfaceName?: string }> = ({
  children,
  interfaceName,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [visible, setVisible] = React.useState(false);
  const isInterfaceLoaded = useInterfaceElementsStore((state) => state.isInterfaceLoaded);
  const isInterfaceLoading = useInterfaceElementsStore((state) => state.isInterfaceLoading);
  const startLoading = useInterfaceElementsStore((state) => state.startLoading);
  const finishLoading = useInterfaceElementsStore((state) => state.finishLoading);
  const setInterfaceElements = useInterfaceElementsStore((state) => state.setInterfaceElements);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);

          if (
            interfaceName &&
            !isInterfaceLoaded(interfaceName) &&
            !isInterfaceLoading(interfaceName)
          ) {
            startLoading(interfaceName);
            getInterfaceElements(interfaceName)
              .then((data) => setInterfaceElements(interfaceName, data))
              .catch(() => finishLoading(interfaceName));
          }

          observer.disconnect();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    interfaceName,
    isInterfaceLoaded,
    isInterfaceLoading,
    startLoading,
    finishLoading,
    setInterfaceElements,
  ]);

  return <div ref={ref}>{visible ? children : <div style={{ minHeight: '350px' }} />}</div>;
};

/** Invisible droppable covering a wrap row (insert into that row). */
const RowDroppable: React.FC<{ rowIdx: number }> = ({ rowIdx }) => {
  const { setNodeRef } = useDroppable({
    id: rowDroppableId(rowIdx),
    data: { type: 'row', rowIdx },
  });

  return (
    <div
      ref={setNodeRef}
      className="absolute pointer-events-auto"
      style={{
        zIndex: 10,
        top: '-8px',
        left: 0,
        right: 0,
        bottom: '-8px',
      }}
    />
  );
};

/** Props for {@link DashboardLayoutRow}. */
interface DashboardLayoutRowProps {
  row: VisualRow;
  rowIdx: number;
  activeId: string | null;
  editMode: boolean;
  previewMode: boolean;
  previewWidths: Record<string, ElementWidth>;
  dropPosition: { targetRowIdx: number; insertIdx: number } | null;
  sourceSectionHeight: number;
  onResize: (sectionId: string, width: ElementWidth) => void;
  onToggleVisibility?: (sectionId: string) => void;
  renderSectionContent: (section: DashboardSection) => React.ReactNode;
}

/** One dashboard row of up to three resizable section slots. */
const DashboardLayoutRow: React.FC<DashboardLayoutRowProps> = ({
  row,
  rowIdx,
  activeId,
  editMode,
  previewMode,
  previewWidths,
  dropPosition,
  sourceSectionHeight,
  onResize,
  onToggleVisibility,
  renderSectionContent,
}) => {
  const canAcceptDrop = !!activeId && row.sections.length < DASHBOARD_MAX_SECTIONS_PER_ROW;
  const rowItems = buildRowItems(row, rowIdx, dropPosition);

  return (
    <div className="col-span-12 flex gap-4 relative" id={`row-container-${rowIdx}`}>
      {canAcceptDrop && <RowDroppable rowIdx={rowIdx} />}
      {rowItems.map((rItem) => {
        if (rItem.kind === 'drop-placeholder') {
          return (
            <div
              key="drop-placeholder"
              className={cn('relative', DASHBOARD_WIDTH_CLASSES[rItem.width])}
            >
              <InlineDropPlaceholder height={sourceSectionHeight} />
            </div>
          );
        }

        const section = rItem.section;
        const effectiveWidth = previewWidths[section.id] || section.width;
        const isPreviewMode = !!previewWidths[section.id];

        return (
          <div key={section.id} className={cn('relative', DASHBOARD_WIDTH_CLASSES[effectiveWidth])}>
            <Section
              id={section.id}
              globalEditMode={editMode}
              isDraggingAny={!!activeId}
              width={effectiveWidth}
              previewMode={isPreviewMode}
              dropIndicator={null}
              rowSectionCount={row.sections.length}
              onResize={(width) => onResize(section.id, width)}
              isVisible={section.visible ?? true}
              onToggleVisibility={
                onToggleVisibility ? () => onToggleVisibility(section.id) : undefined
              }
              className={cn(
                activeId ? '' : 'transition-all duration-300',
                editMode && 'animate-pulse-slow',
              )}
            >
              <ErrorBoundary
                fallback={
                  <div className="p-8 text-center text-muted-foreground">
                    {getErrorMessage('loadDashboardSectionFailed')}
                  </div>
                }
              >
                {previewMode ? (
                  renderSectionContent(section)
                ) : (
                  <LazyRender interfaceName={getSectionIEResource(section.type) ?? undefined}>
                    {renderSectionContent(section)}
                  </LazyRender>
                )}
              </ErrorBoundary>
            </Section>
          </div>
        );
      })}
    </div>
  );
};

/** Props for {@link DashboardVisualGrid}. */
interface DashboardVisualGridProps {
  sections: DashboardSection[];
  activeId: string | null;
  editMode: boolean;
  previewMode: boolean;
  previewWidths: Record<string, ElementWidth>;
  dropPosition: { targetRowIdx: number; insertIdx: number } | null;
  sourceSectionHeight: number;
  onResize: (sectionId: string, width: ElementWidth) => void;
  onToggleVisibility?: (sectionId: string) => void;
  renderSectionContent: (section: DashboardSection) => React.ReactNode;
}

/** Visual grid of dashboard rows used in edit and preview modes. */
const DashboardVisualGrid: React.FC<DashboardVisualGridProps> = ({
  sections,
  activeId,
  editMode,
  previewMode,
  previewWidths,
  dropPosition,
  sourceSectionHeight,
  onResize,
  onToggleVisibility,
  renderSectionContent,
}) => {
  const visualItems = resolveVisualItems(sections, activeId);

  return (
    <>
      {visualItems.map((item) => {
        if (item.kind === 'drop-zone') {
          return (
            <DropZoneBetweenRows
              key={`dz-${item.afterRowIdx}`}
              afterRowIdx={item.afterRowIdx}
              visible={editMode && !!activeId}
            />
          );
        }

        if (item.kind === 'source-placeholder') {
          return <SourceRowPlaceholder key="source-placeholder" height={sourceSectionHeight} />;
        }

        return (
          <DashboardLayoutRow
            key={`row-${item.rowIdx}`}
            row={item.row}
            rowIdx={item.rowIdx}
            activeId={activeId}
            editMode={editMode}
            previewMode={previewMode}
            previewWidths={previewWidths}
            dropPosition={dropPosition}
            sourceSectionHeight={sourceSectionHeight}
            onResize={onResize}
            onToggleVisibility={onToggleVisibility}
            renderSectionContent={renderSectionContent}
          />
        );
      })}
    </>
  );
};

/** Editable dashboard grid: wrap rows, drag-and-drop, and lazy-mounted widgets. */
export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  sections,
  onSectionsChange,
  editMode,
  previewMode = false,
  scale = 1,
  onUnsavedChanges,
  onToggleVisibility,
}) => {
  const { mode } = useMode();

  /** Prefer source placeholder, then between-row zones, then in-row droppables. */
  const customCollisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);

    if (pointerCollisions.length > 0) {
      const sourcePlaceholder = pointerCollisions.find(
        ({ id }) => id === SOURCE_ROW_PLACEHOLDER_ID,
      );
      if (sourcePlaceholder) return [sourcePlaceholder];

      const dropZone = pointerCollisions.find(({ id }) => isDropZoneAfterRowId(String(id)));
      if (dropZone) return [dropZone];

      const rowDroppable = pointerCollisions.find(({ id }) => isRowDroppableId(String(id)));
      if (rowDroppable) return [rowDroppable];
      return pointerCollisions;
    }

    const centerCollisions = closestCenter(args);

    const rowDroppable = centerCollisions.find(({ id }) => isRowDroppableId(String(id)));
    if (rowDroppable) return [rowDroppable];

    return centerCollisions;
  };
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [previewWidths, setPreviewWidths] = React.useState<Record<string, ElementWidth>>({});
  const [, setDropIndicator] = React.useState<{
    sectionId: string;
    position: 'left' | 'right';
  } | null>(null);
  const [dropPosition, setDropPosition] = React.useState<{
    targetRowIdx: number;
    insertIdx: number;
  } | null>(null);

  const sectionRectsSnapshot = React.useRef<Record<string, DOMRect>>({});
  const previewWidthsRef = React.useRef<Record<string, ElementWidth>>({});
  const dropPositionRef = React.useRef<{ targetRowIdx: number; insertIdx: number } | null>(null);
  const dropIndicatorRef = React.useRef<{
    sectionId: string;
    position: 'left' | 'right';
  } | null>(null);
  const cursorXRef = React.useRef<number>(0);

  const activeIdRef = React.useRef<string | null>(null);
  const currentHoverRowIdxRef = React.useRef<number | null>(null);
  const currentHoverRowSectionCountRef = React.useRef<number>(0);
  const currentHoverRowSectionsRef = React.useRef<DashboardSection[]>([]);
  const sourceSectionHeightRef = React.useRef<number>(DASHBOARD_DEFAULT_SOURCE_SECTION_HEIGHT);

  /** Skip a preview-width update when the map is unchanged (avoids hover flicker). */
  const setStablePreviewWidths = React.useCallback((newWidths: Record<string, ElementWidth>) => {
    if (JSON.stringify(newWidths) !== JSON.stringify(previewWidthsRef.current)) {
      previewWidthsRef.current = newWidths;
      setPreviewWidths(newWidths);
    }
  }, []);

  /** Clear preview widths only if any are set. */
  const setStablePreviewWidthsEmpty = React.useCallback(() => {
    if (Object.keys(previewWidthsRef.current).length > 0) {
      previewWidthsRef.current = {};
      setPreviewWidths({});
    }
  }, []);

  /** Skip drop-position updates when insert target/index is unchanged. */
  const setStableDropPosition = React.useCallback(
    (val: { targetRowIdx: number; insertIdx: number } | null) => {
      if (JSON.stringify(val) !== JSON.stringify(dropPositionRef.current)) {
        dropPositionRef.current = val;
        setDropPosition(val);
      }
    },
    [],
  );

  /** Skip indicator updates when neighbor + side is unchanged. */
  const setStableDropIndicator = React.useCallback(
    (val: { sectionId: string; position: 'left' | 'right' } | null) => {
      if (JSON.stringify(val) !== JSON.stringify(dropIndicatorRef.current)) {
        dropIndicatorRef.current = val;
        setDropIndicator(val);
      }
    },
    [],
  );

  React.useEffect(() => {
    const onMove = (e: PointerEvent) => {
      cursorXRef.current = e.clientX;

      const hoverRowIdx = currentHoverRowIdxRef.current;
      if (hoverRowIdx === null || !activeIdRef.current) return;

      const rowContainer = document.getElementById(`row-container-${hoverRowIdx}`);
      if (!rowContainer) return;

      const rect = rowContainer.getBoundingClientRect();
      const indicator = computeRowInsertIndicator(
        currentHoverRowSectionCountRef.current,
        e.clientX - rect.left,
        rect.width,
        currentHoverRowSectionsRef.current,
      );
      if (!indicator) return;

      const positionChanged =
        dropPositionRef.current?.targetRowIdx !== hoverRowIdx ||
        dropPositionRef.current?.insertIdx !== indicator.insertIdx;
      if (!positionChanged) return;

      dropPositionRef.current = { targetRowIdx: hoverRowIdx, insertIdx: indicator.insertIdx };
      setDropPosition({ targetRowIdx: hoverRowIdx, insertIdx: indicator.insertIdx });

      if (!indicator.sectionId) return;
      dropIndicatorRef.current = { sectionId: indicator.sectionId, position: indicator.position };
      setDropIndicator({ sectionId: indicator.sectionId, position: indicator.position });
    };
    window.addEventListener('pointermove', onMove, { capture: true });
    return () => window.removeEventListener('pointermove', onMove, { capture: true });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** Clear hover + overlay state after drop or cancel. */
  const resetDragState = React.useCallback(() => {
    setActiveId(null);
    activeIdRef.current = null;
    currentHoverRowIdxRef.current = null;
    previewWidthsRef.current = {};
    setPreviewWidths({});
    dropPositionRef.current = null;
    setDropPosition(null);
    dropIndicatorRef.current = null;
    setDropIndicator(null);
    document.body.classList.remove('is-dragging');
  }, []);

  /** Commit insert into a wrap row or a new row after a between-row drop zone. */
  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      resetDragState();
      return;
    }

    const overId = over.id as string;
    const activeIdStr = active.id as string;

    if (overId === SOURCE_ROW_PLACEHOLDER_ID) {
      resetDragState();
      return;
    }

    onSectionsChange(applySectionDragEnd(sections, activeIdStr, overId, dropPositionRef.current));

    if (onUnsavedChanges) {
      onUnsavedChanges(true);
    }

    resetDragState();
  };

  /** Clear in-row hover preview when the pointer leaves a valid target. */
  const resetHoverState = React.useCallback(() => {
    currentHoverRowIdxRef.current = null;
    setStablePreviewWidthsEmpty();
    setStableDropPosition(null);
    setStableDropIndicator(null);
  }, [setStablePreviewWidthsEmpty, setStableDropPosition, setStableDropIndicator]);

  /** Hovering a wrap row: preview equal widths and compute insert index from cursor X. */
  const applyRowDroppableHover = (
    overId: string,
    activeIdStr: string,
    rowsWithoutActive: VisualRow[],
  ) => {
    const rowIdx = parseRowDroppableIdx(overId);
    const targetRow = rowsWithoutActive[rowIdx];

    if (!targetRow || targetRow.sections.length >= DASHBOARD_MAX_SECTIONS_PER_ROW) {
      resetHoverState();
      return;
    }

    currentHoverRowIdxRef.current = rowIdx;
    currentHoverRowSectionCountRef.current = targetRow.sections.length;
    currentHoverRowSectionsRef.current = targetRow.sections;
    setStablePreviewWidths(calculateAllRowsPreview(rowsWithoutActive, rowIdx, activeIdStr));

    const rowContainer = document.getElementById(`row-container-${rowIdx}`);
    if (!rowContainer) return;

    const rect = rowContainer.getBoundingClientRect();
    const insertIdx = computeRowInsertIdx(
      targetRow.sections.length,
      cursorXRef.current - rect.left,
      rect.width,
    );
    setStableDropPosition({ targetRowIdx: rowIdx, insertIdx });
  };

  /** Hovering a between-row gap: preview the dragged section as a full-width new row. */
  const applyDropZoneAfterRowHover = (activeIdStr: string, rowsWithoutActive: VisualRow[]) => {
    currentHoverRowIdxRef.current = null;
    const newWidths: Record<string, ElementWidth> = { [activeIdStr]: 'full' };
    applyEqualRowWidths(rowsWithoutActive, newWidths);
    setStablePreviewWidths(newWidths);
    setStableDropPosition(null);
    setStableDropIndicator(null);
  };

  /** Route hover to in-row insert vs between-row new-row preview. */
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      resetHoverState();
      return;
    }

    const overId = over.id as string;
    const activeIdStr = active.id as string;
    activeIdRef.current = activeIdStr;

    if (overId === SOURCE_ROW_PLACEHOLDER_ID) {
      resetHoverState();
      return;
    }

    const sectionsWithoutActive = sections.filter((s) => s.id !== activeIdStr);
    const rowsWithoutActive = calculateRows(sectionsWithoutActive);

    if (isRowDroppableId(overId)) {
      applyRowDroppableHover(overId, activeIdStr, rowsWithoutActive);
      return;
    }

    if (isDropZoneAfterRowId(overId)) {
      applyDropZoneAfterRowHover(activeIdStr, rowsWithoutActive);
    }
  };

  /** Resize one section and keep its wrap-row neighbor complementary (2-up only). */
  const handleResize = (sectionId: string, width: ElementWidth) => {
    onSectionsChange(applySectionResize(sections, sectionId, width));

    if (onUnsavedChanges) {
      onUnsavedChanges(true);
    }
  };

  /** Preview mode: icon placeholder. Live mode: remote vs in-person section by `type`. */
  const renderSectionContent = (section: DashboardSection) => {
    if (previewMode) {
      const Icon = getLucideIcon(getSectionIcon(section.type));
      return (
        <div className="section-placeholder">
          <Icon size={48} />
          <h3>{section.title}</h3>
          <p className="text-muted">{section.type}</p>
        </div>
      );
    }

    switch (section.type) {
      case 'stats':
        return <KpiSection />;
      case 'postits':
        return <PostItsSection sectionWidth={section.width} />;
      case 'sessions':
        return mode === 'remote' ? (
          <SessionsRemoteSection layoutWidth={section.width} />
        ) : (
          <SessionsSection layoutWidth={section.width} />
        );
      case 'pairs':
        return mode === 'remote' ? (
          <PairsRemoteSection layoutWidth={section.width} />
        ) : (
          <PairsSection layoutWidth={section.width} />
        );
      case 'establishments':
        return <EstablishmentsSection layoutWidth={section.width} />;
      case 'tutors':
        return mode === 'remote' ? (
          <TutorsRemoteSection layoutWidth={section.width} />
        ) : (
          <TutorsInPersonSection layoutWidth={section.width} />
        );
      case 'students':
        return mode === 'remote' ? (
          <StudentsRemoteSection layoutWidth={section.width} />
        ) : (
          <StudentsInPersonSection layoutWidth={section.width} />
        );
      case 'volunteers':
        return <VolunteersSection layoutWidth={section.width} />;
      case 'admins':
        return <AdminsSection layoutWidth={section.width} />;
      case 'calendar':
        return <CalendarSection layoutWidth={section.width} />;
      case 'sessions-chart':
        return <SessionsChartSection />;
      case 'resources-chart':
        return <ResourcesChartSection />;
      case 'reports':
        return mode === 'remote' ? (
          <ReportsRemoteSection layoutWidth={section.width} />
        ) : (
          <ReportsSection layoutWidth={section.width} />
        );
      case 'vscReports':
        return <VscReportsSection layoutWidth={section.width} />;
      case 'assessments':
        return <AssessmentsSection layoutWidth={section.width} />;
      case 'subjects':
        return <SubjectsSection layoutWidth={section.width} />;
      case 'levels':
        return <LevelsSection layoutWidth={section.width} />;
      case 'programs':
        return <ProgramsSection layoutWidth={section.width} />;
      case 'schoolYears':
        return <SchoolYearsSection layoutWidth={section.width} />;
      case 'activityTypes':
        return <ActivityTypesSection layoutWidth={section.width} />;
      case 'activities':
        return <ActivitiesSection layoutWidth={section.width} />;
      case 'holidays':
        return <HolidaysSection layoutWidth={section.width} />;
      case 'events':
        return <EventsAndSummariesSection layoutWidth={section.width} />;
      case 'parents':
        return <ParentsSection layoutWidth={section.width} />;
      case 'partners':
        return <PartnersSection layoutWidth={section.width} />;
      case 'sport-clubs':
        return <SportClubsSection layoutWidth={section.width} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        autoScroll={{
          enabled: true,
          threshold: { x: 0.1, y: 0.15 },
          acceleration: 10,
          interval: 10,
        }}
        onDragEnd={handleSectionDragEnd}
        onDragStart={(event: DragStartEvent) => {
          const id = event.active.id as string;
          setActiveId(id);
          activeIdRef.current = id;
          document.body.classList.add('is-dragging');

          const activeEl = document.getElementById(id);
          if (activeEl) {
            sourceSectionHeightRef.current = activeEl.getBoundingClientRect().height;
          }
          if (!sourceSectionHeightRef.current) {
            sourceSectionHeightRef.current = DASHBOARD_DEFAULT_SOURCE_SECTION_HEIGHT;
          }

          sectionRectsSnapshot.current = {};
          sections.forEach((s) => {
            if (s.id !== id) {
              const el = document.getElementById(s.id);
              if (el) sectionRectsSnapshot.current[s.id] = el.getBoundingClientRect();
            }
          });
        }}
        onDragOver={handleDragOver}
        onDragCancel={resetDragState}
      >
        <SortableContext items={sections.map((s) => s.id)}>
          <DashboardDragOverlayPortal activeId={activeId} sections={sections} />
          <div className="grid grid-cols-12 gap-6 items-stretch">
            <DashboardVisualGrid
              sections={sections}
              activeId={activeId}
              editMode={editMode}
              previewMode={previewMode}
              previewWidths={previewWidths}
              dropPosition={dropPosition}
              sourceSectionHeight={sourceSectionHeightRef.current}
              onResize={handleResize}
              onToggleVisibility={onToggleVisibility}
              renderSectionContent={renderSectionContent}
            />
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
